<template>
	<v-container class="contentRow" style="margin-top: -12px;">
		<v-col cols="12" class="text-center">
			<h1>Risk Definition</h1>
			<p style="margin: 30px 50px;">On the risk definition pages, you
			can set probability and consequence levels that align with your company’s risk tolerance, ensuring a clear and
			structured approach to risk management.
			</p>
			<p style="margin-left: 10px;" class="text-left">To assess risks effectively, use the formula <strong>Risk =
					Consequence x Probability</strong>. Start by identifying relevant risks, then establish a scale for
				consequences and define probabilities. This approach enables a clear evaluation of each risk's potential
				impact.</p>
			<v-btn class="addNewRisk-button" @click="goToRiskMatrixPage" color="#E97132"
				style="color: white; margin-bottom: 10px; display: flex;">More Info...</v-btn>
			<br>
			<h2 class="text-left">Define Consequence</h2>
			<br>
			<p style="margin-left: 10px;" class="text-left">In the table below consequence and probability level scored as 1
				equals point 1-5 in the risk matrix and risk definition table, level 2 equals 6-10, level 3 equals 11-15,
				level 4 equals 16-20 and level 5 equals 21-25. This gives you the opportunity to have several risks placed
				in the same risk level per risk matrix.</p>
			<div class="addNewRisk-box">
				<v-autocomplete
					class="addNewRisk-input"
					v-model="newRowHeading"
					:items="riskCategories"
					label="Select Risk Category"
					dense
					outlined
					clearable
					hide-selected
					@change="addNewRow"
				/>
			</div>

			<v-data-table :headers="headers" :items="connections" class="light table" hide-default-footer>
				<template v-slot:item.Risk="{ item }">
					<div>
						<span>{{ item.Risk }}</span>
						<button v-if="item.newlyAdded" @click="deleteRow(item)" class="custom-delete-btn" :disabled="valid">
							<v-icon class="custom-delete-icon" :disabled="valid">fa-solid fa-trash </v-icon>
						</button>
					</div>
				</template>
				<template v-slot:item.One="{ item }">
					<textarea v-model="item.One" v-auto-grow class="small-input"
						:placeholder="getPlaceholder1(item.Risk, 1)" :disabled="valid"></textarea>
				</template>
				<template v-slot:item.Two="{ item }">
					<textarea v-model="item.Two" v-auto-grow class="small-input" :disabled="valid"></textarea>
				</template>
				<template v-slot:item.Three="{ item }">
					<textarea v-model="item.Three" v-auto-grow class="small-input" :disabled="valid"></textarea>
				</template>
				<template v-slot:item.Four="{ item }">
					<textarea v-model="item.Four" v-auto-grow class="small-input" :disabled="valid"></textarea>
				</template>
				<template v-slot:item.Five="{ item }">
					<textarea v-model="item.Five" v-auto-grow class="small-input"
						:placeholder="getPlaceholder5(item.Risk, 1)" :disabled="valid"></textarea>
				</template>
			</v-data-table>
			<div class="button-container">
				<template v-if="hasData">
					<v-btn color="primary" class="save-all-button" @click="deleteAllEdits">Delete All</v-btn>
					<v-btn color="primary" class="save-all-button" @click="editAllRows">{{ isEditing ? 'Save Changes' :
						'Edit All' }}</v-btn>
				</template>
				<template v-else>
					<v-btn color="primary" class="save-all-button" @click="clearAllEdits">Clear All</v-btn>
					<v-btn color="primary" class="save-all-button" @click="saveAllEdits">Save All</v-btn>
				</template>
			</div>
		</v-col>
	</v-container>
</template>

<script>
import { riskAPI } from "../api/csr/riskdef"
import LoadingState from "../consts/LoadingState"

export default {
	data() {
		return {
			newRowHeading: "",
			connections: [
				{ Risk: "Corruption", One: "", Two: "", Three: "", Four: "", Five: "", newlyAdded: false },
				{ Risk: "Labor", One: "", Two: "", Three: "", Four: "", Five: "", newlyAdded: false },
				{ Risk: "Environment", One: "", Two: "", Three: "", Four: "", Five: "", newlyAdded: false },
				{ Risk: "Human Rights", One: "", Two: "", Three: "", Four: "", Five: "", newlyAdded: false },
			],
			headers: [
				{ text: "Type of Risk", align: "start", value: "Risk", sortable: false },
				{ text: "1", value: "One", sortable: false },
				{ text: "2", value: "Two", sortable: false },
				{ text: "3", value: "Three", sortable: false },
				{ text: "4", value: "Four", sortable: false },
				{ text: "5", value: "Five", sortable: false }
			],
			savedSuccessfully: false,
			hasData: false,
			valid: false,
			isEditing: false,
			iDs: [],
			riskCategories: [
				"Country",
				"Financial",
				"Gender Rights",
				"Management",
				"Market",
				"Production",
				"Quality",
				"Supply Chains",
				"Technological",
				"Water Use"
			],
		}
	},
	mounted() {
		this.loadItems()
	},
	methods: {
		getPlaceholder1(rowIndex, colIndex) {
			const placeholders = 
				[
					"Ex- Conflict of interest", 
					"Ex- Negative Stress", 
					"Ex- Poor waste management", 
					"Ex- Inadequate breaks", 
				]

			if(rowIndex=="Corruption" && colIndex ==1){
				return(placeholders[0])
			}else if(rowIndex=="Labor" && colIndex ==1){
				return(placeholders[1])
			}else if(rowIndex=="Environment" && colIndex ==1){
				return(placeholders[2])
			}else if(rowIndex=="Human Rights" && colIndex ==1){
				return(placeholders[3])
			}else {
				return("")
			}
			
		},
		getPlaceholder5(rowIndex, colIndex) {
			const placeholders = 
				[
					"Ex- Fund embezzlement​", 
					"Ex- Loss Of Life​", 
					"Ex-Severe Ecological Damage", 
					"Ex-Forced labor", 
				]

			if(rowIndex=="Corruption" && colIndex ==1){
				return(placeholders[0])
			}else if(rowIndex=="Labor" && colIndex ==1){
				return(placeholders[1])
			}else if(rowIndex=="Environment" && colIndex ==1){
				return(placeholders[2])
			}else if(rowIndex=="Human Rights" && colIndex ==1){
				return(placeholders[3])
			}else {
				return("")
			}
			
		},
		loadItems() {
			this.$store.dispatch("loading/" + LoadingState.ACTION_START_LOADING, null, { root: true })
			riskAPI.getAll()
				.then((data) => {
					console.log("Data fetched from API:", data)
					this.hasData = data[0].length !== 0
					this.valid = data[0].length !== 0
					if (data.length > 0 && Array.isArray(data[0])) {
						data[0].forEach((entry) => {
							this.iDs.push(entry.ID)

							console.log("Entry:", entry)
							const typeOfRisk = entry.TypeOfRisk
							console.log("TypeOfRisk:", typeOfRisk)
							const existingRisk = this.connections.find(item => item.Risk === typeOfRisk)
							console.log("ExistingRisk:", existingRisk)

							if (existingRisk) {
								existingRisk.One = entry.Rating1 || ""
								existingRisk.Two = entry.Rating2 || ""
								existingRisk.Three = entry.Rating3 || ""
								existingRisk.Four = entry.Rating4 || ""
								existingRisk.Five = entry.Rating5 || ""
							} else {
								this.connections.push({
									Risk: entry.TypeOfRisk,
									One: entry.Rating1 || "",
									Two: entry.Rating2 || "",
									Three: entry.Rating3 || "",
									Four: entry.Rating4 || "",
									Five: entry.Rating5 || "",
									newlyAdded: true
								})
							}
						})
					}
					this.$store.dispatch("loading/" + LoadingState.ACTION_END_LOADING, null, { root: true })
					console.log(this.iDs)
				})
				.catch((error) => {
					console.error("Error fetching data:", error)
					this.$store.dispatch("loading/" + LoadingState.ACTION_SET_ERROR, error, { root: true })
				})
		},
		async addNewRow() {
			if (this.newRowHeading && this.newRowHeading.trim() !== "") {
				const exists = this.connections.some(
					item => item.Risk.toLowerCase() === this.newRowHeading.trim().toLowerCase()
				)
				if (exists) {
					alert("This risk category already exists.")
					this.newRowHeading = ""
					return
				}
				const newRow = { Risk: this.newRowHeading, newlyAdded: true }
				if (this.isEditing) {
					await riskAPI.createRisk(
						this.newRowHeading,
						"",
						"",
						"",
						"",
						"",
					)
					const lastId = this.iDs.length > 0 ? this.iDs[this.iDs.length - 1] : 0
					const newId = lastId + 1
					this.iDs.push(newId)
				}
				this.headers.slice(1).forEach(header => {
					newRow[header.value] = ""
				})
				this.connections.push(newRow)
				this.newRowHeading = ""
			}
		},
		async deleteRow(row) {
			console.log(this.valid)
			const index = this.connections.indexOf(row)
			const did = this.iDs[index]
			console.log(index)
			try {
				await riskAPI.deleteRisk(did)
				this.iDs.splice(index, 1)
			} catch (error) {
				console.log("An error occurred while saving the risks.")
			}
			console.log(this.iDs)
			if (index > -1) {
				this.connections.splice(index, 1)
			}
		},
		async editAllRows() {
			console.log("Valid before set to false:", this.valid)
			this.valid = false
			console.log("Valid set to false:", this.valid)
			this.isEditing = !this.isEditing
			this.valid = !this.isEditing
			if (!this.isEditing) {
				try {
					for (const [index, item] of this.connections.entries()) {
						item.ID = this.iDs[index]
						await riskAPI.updateRiskById(
							item.ID,
							item.One,
							item.Two,
							item.Three,
							item.Four,
							item.Five
						)
					}
					alert("All risks have been saved successfully.")
					this.valid = true
					this.reloadComponent()
				} catch (error) {
					console.error("Error saving risks:", error)
					alert("An error occurred while saving the risks.")
				}
			}
		},
		async deleteAllEdits() {
			try {
				for (const [index, item] of this.connections.entries()) {
					item.ID = this.iDs[index]
					await riskAPI.updateRiskById(
						item.ID,
						"",
						"",
						"",
						"",
						"",
					)
				}

				this.valid = true
				this.reloadComponent()
			} catch (error) {
				console.error("Error saving risks:", error)

			}
		},
		clearAllEdits() {
			this.connections.forEach(item => {
				item.One = ""
				item.Two = ""
				item.Three = ""
				item.Four = ""
				item.Five = ""
			})
		},
		goToRiskMatrixPage() {
			this.$router.push({
				path: "/knowledge",
				query: { article: "Sustainability Tutorial", doc: "risk-definition" }
			})
		},
		async saveAllEdits() {
			try {
				for (const item of this.connections) {
					await riskAPI.createRisk(
						item.Risk,
						item.One,
						item.Two,
						item.Three,
						item.Four,
						item.Five
					)
				}
				alert("All risks have been saved successfully.")
				this.valid = true
				this.reloadComponent()
			} catch (error) {
				console.error("Error saving risks:", error)
				alert("An error occurred while saving the risks.")
			}
		},
		reloadComponent() {
			this.savedSuccessfully = false
			this.loadItems()
			this.iDs = []
		}
	},

	directives: {
		"auto-grow": {
			update: function (el) {
				el.style.height = "auto"
				el.style.height = (el.scrollHeight) + "px"
			}
		}
	}
}
</script>



<style>
.v-data-table .v-data-table-header tr th:first-child,
.v-data-table .v-data-table-body tr td:first-child {
	width: 200px;
}

.v-data-table .v-data-table-header tr th:not(:first-child) {
	padding-left: 30px;
}

/* .custom-header {
    font-size: 15px;
    border-right: 1px solid #ccc; 
    background-color: #217b7f; 
    color: #ffffff; 
}

.v-data-table .v-data-table-header tr th {
    font-size: 15px !important;
    color: #ffffff !important; 
    background-color: #217b7f; 
    border-right: 1px solid #ccc; 
} */

.v-data-table .v-data-table-body tr td {
	border-right: 1px solid #ccc;
}

.v-data-table .v-data-table-body tr td:last-child {
	border-right: none;
}

.small-input {
	width: 100%;
	position: relative;
	height: auto;
	overflow-wrap: break-word;
	word-wrap: break-word;
	resize: none;
	overflow-y: hidden;
	border: none;
	background-color: #fff;
	color: #000;
	padding: 5px;
	box-sizing: border-box;
	transition: background-color 0.3s ease;
}

.small-input:disabled {
	color: rgb(78, 78, 78);

}

.small-input:hover {
	background-color: #f9f9f9;
}

.small-input:focus {
	outline: none;
}

.addNewRisk-box {
	margin-left: 1rem;
	margin-right: 1rem;
	display: flex;
	justify-content: flex-end;
	align-items: center;
	margin-bottom: 20px;
}

.addNewRisk-input {
	width: 180px;
	height: 40px;
	padding: 10px;
	border: 1px solid #ccc;
	border-radius: 4px;
	font-size: 16px;
	box-sizing: border-box;
}

.addNewRisk-button {
	margin-left: 10px;
}

.custom-delete-btn {
	background-color: white !important;
	box-shadow: none !important;
	margin-left: 20px;
}

.custom-delete-btn :disabled {
	background-color: white !important;
	box-shadow: none !important;

}

.custom-delete-icon {
	font-size: 16px !important;
	/* Adjust the font size to make the icon smaller */
	color: #1e7c83 !important;
	/* Make the bin icon green */
	color: inherit;
	width: 2px !important;
}

.custom-delete-icon :disabled {

	color: rgb(129, 128, 128) !important;

}

.button-container {
	display: flex;
	justify-content: flex-end;
	padding-right: 10px;
	margin-top: 20px;
}

.save-all-button {
	font-size: 20px;
	background-color: #217b7f;
	color: #fff;
	padding: 5px 10px;
	margin-left: 20px;
	border: none;
	cursor: pointer;
	border-radius: 4px;
}
</style>
